import { useState } from "react";
import { Button, Card, Col, ConfigProvider, DatePicker, Flex, Row, Select, Segmented, Tag } from "antd";
import { CalendarOutlined, CheckCircleOutlined, SwapOutlined } from "@ant-design/icons";
import { getPackageComparisonOverview } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { tnum } from "shared/components/TableText";
import { formatUpgrades } from "shared/utils";
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_SUBMIT_FORMAT, now, tzDate } from "shared/utils/dayjs";
import { AnalyticsRange, CustomDayRange, PackageAnalyticsRow, PackageType } from "shared/types";
import RangeSegmented, { pillSegmentedTheme } from "./RangeSegmented";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import PackageCompareRevenueChart from "./PackageCompareRevenueChart";
import PackageCompareCountsChart from "./PackageCompareCountsChart";
import { useAuth } from "context/AuthContextProvider";
import { analyticsRangeStamp, customDayStamp } from "shared/utils/download";
import { useTableSort } from "hooks/useTableSort";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { formatDateRangeOrDash, formatMoneyOrDash } from "shared/reports/columnCatalogue";
import { ProductStatusTag } from "./ProductNameCell";
import { textPx } from "shared/constants/zoom";

// Bandingkan is a manually-curated small comparison, not a big-N report —
// selecting past this many items is already outside intended use. Only the
// two chart components are capped; the Select picker and the Table below
// stay fully uncapped.
const COMPARE_CHART_MAX_ITEMS = 8;

const { RangePicker } = DatePicker;

interface PackageComparisonContentProps {
  type: PackageType;
  title: string;
  backTo: string;
}

interface CompareOption {
  value: number;
  label: string;
  row: PackageAnalyticsRow;
}

const ChartLegend = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap={6}>
    <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
    <span style={{ fontSize: textPx(12), color: colors.ink2 }}>{label}</span>
  </Flex>
);

// Shared with the table's own column `sorter`s below, so the comparator
// logic isn't duplicated — used to reorder the admin's picks outside AntD's
// table (charts, PDF export), which don't go through AntD's own sort.
const SORT_COMPARATORS: Record<string, (a: PackageAnalyticsRow, b: PackageAnalyticsRow) => number> = {
  unique_clients: (a, b) => a.unique_clients - b.unique_clients,
  sold: (a, b) => a.sold - b.sold,
  upgrades: (a, b) => (a.upgrades ?? 0) - (b.upgrades ?? 0),
  sales: (a, b) => a.sales - b.sales,
  last_purchase: (a, b) => (a.last_purchase ?? "").localeCompare(b.last_purchase ?? ""),
};

// Manual multi-picker comparison — deliberately not an auto-matched "similar
// package" suggestion (fragile: same class type? closest credit count?
// closest price? no single rule matches admin intuition). Works for both
// requested use cases (tiers of the same product, or a promo vs. a regular
// product) with one UI, since the admin just picks any of the rows — no cap
// on how many at once, the grid just wraps onto more rows.
//
// Mixed reguler+promo, always all-time (see getPackageComparisonOverview) —
// same reasoning as PendapatanProdukPromoContent: a bounded-lifetime promo on
// one side would make a rolling day-range look arbitrary, so this report
// skips the range picker entirely and shows lifetime totals for every side.
const PackageComparisonContent = ({ type, title, backTo }: PackageComparisonContentProps) => {
  const { state: authState } = useAuth();
  const [allTime, setAllTime] = useState(true);
  const [range, setRange] = useState<AnalyticsRange>("1m");
  const [customDayRange, setCustomDayRange] = useState<CustomDayRange>(null);
  const { data: overview, isLoading } = getPackageComparisonOverview(
    type,
    allTime,
    range,
    customDayRange,
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { sortState, onSortChange } = useTableSort();

  const table = overview?.table ?? [];
  const pickedRows = selectedIds
    .map((id) => table.find((r) => r.package_id === id))
    .filter((r): r is PackageAnalyticsRow => !!r);
  // Reorders the admin's picks by whichever column they've sorted the
  // table by, falling back to pick-order when nothing's sorted — so the
  // table's PDF export (which doesn't go through AntD's own client-side
  // sort) reflects the same row order the on-screen table shows, instead of
  // always freezing at "the order items were picked." The charts
  // deliberately do NOT follow this — they stay in pick-order regardless of
  // table sort (see chartRows below), since re-ordering the bars every time
  // the table's sorted would make them a moving target rather than a stable
  // reference alongside the table.
  const activeComparator = sortState ? SORT_COMPARATORS[sortState.key] : undefined;
  const selectedRows =
    activeComparator && sortState
      ? [...pickedRows].sort((a, b) =>
          sortState.order === "descend" ? -activeComparator(a, b) : activeComparator(a, b),
        )
      : pickedRows;
  // The two charts render only the first N selections, always in pick-order
  // — independent of whatever the table below is currently sorted by.
  const chartRows = pickedRows.slice(0, COMPARE_CHART_MAX_ITEMS);

  // The export mirrors exactly what's on screen: the admin's current
  // selection under the current time mode, not the whole catalogue (that's
  // "Performa Paket/Membership"). package_ids is what makes the workbook a
  // comparison rather than a dump — see bandingkanProdukSpec on the server.
  const exportParams = {
    type,
    package_ids: selectedIds.join(","),
    ...(allTime
      ? { all_time: "true" }
      : customDayRange
        ? { from_date: customDayRange.from, to_date: customDayRange.to }
        : { range }),
  };
  const exportStamp = allTime
    ? `sepanjangwaktu-${now().format("YYYYMMDD")}`
    : customDayRange
      ? customDayStamp(customDayRange)
      : analyticsRangeStamp(range, null);
  // The server's range_label is Indonesian ("Sepanjang Waktu" in all-time
  // mode); in that mode render the translated label instead, on screen and
  // in the PDF. Date ranges stay as the server prints them (locale-neutral).
  const rangeLabelDisplay = allTime
    ? translate("analyticsPage.package.compare_mode_all_time")
    : overview?.range_label;

  // Grouped so Reguler/Promo are easy to scan apart in a long list — same
  // split already used on the Paket/Membership catalog pages, just applied
  // to this picker instead of a new filter control.
  const toOption = (r: PackageAnalyticsRow): CompareOption => ({
    value: r.package_id,
    label: r.name,
    row: r,
  });
  const options = [
    {
      label: translate("product_catalog.tab_reguler"),
      options: table.filter((r) => !r.is_promo).map(toOption),
    },
    {
      label: translate("product_catalog.tab_promo"),
      options: table.filter((r) => r.is_promo).map(toOption),
    },
  ];

  // This row's promo window as a day range the picker can hold. The stored
  // bounds are instants at Jakarta day edges, so the dates are read in
  // Jakarta: the start is 00:00 WIB, which is still the previous day in UTC.
  // Like any picked range it never reaches past today (nothing after today
  // can have happened yet), and a promo that has not started has no range.
  const promoPeriodRange = (row: PackageAnalyticsRow): CustomDayRange => {
    if (!row.promo_starts_at || !row.promo_ends_at) return null;
    const today = now();
    const start = tzDate(row.promo_starts_at);
    if (start.isAfter(today, "day")) return null;
    const end = tzDate(row.promo_ends_at);
    return {
      from: start.format(DEFAULT_DATE_SUBMIT_FORMAT),
      to: (end.isAfter(today, "day") ? today : end).format(DEFAULT_DATE_SUBMIT_FORMAT),
    };
  };

  // Sets the day-precise range to this row's own promo window in one click —
  // the dates are already known data, no reason to make the admin hand-copy
  // them into the picker.
  const applyPromoPeriod = (row: PackageAnalyticsRow) => {
    const period = promoPeriodRange(row);
    if (!period) return;
    setAllTime(false);
    setCustomDayRange(period);
  };

  // Whether the active range already IS this row's own promo window — lets
  // renderSpecs swap the action for a quiet "already applied" badge instead
  // of offering a no-op click, and (with multiple promo cards selected)
  // makes it visible at a glance which one's numbers reflect its own period.
  const isPromoPeriodApplied = (row: PackageAnalyticsRow) => {
    const period = promoPeriodRange(row);
    return (
      !allTime &&
      !!customDayRange &&
      !!period &&
      period.from === customDayRange.from &&
      period.to === customDayRange.to
    );
  };

  const renderOption = (option: unknown) => {
    const row = (option as { data: CompareOption }).data.row;
    return (
      <Flex gap={8} align="center" wrap="wrap">
        <span>{row.name}</span>
        <ProductStatusTag row={row} />
      </Flex>
    );
  };

  // Same standard columns+dataSource Table every other analytics report
  // uses (see PendapatanProdukPromoContent) rather than a bespoke card grid —
  // items as rows, metrics as columns, so a single metric (e.g. "Terjual")
  // reads straight down one column across every selected item, and this
  // slots into the same shape as everything else if/when reports gain PDF
  // export. Only col_name is namespace-aware (Paket vs. Membership); every
  // other label reuses the shared "package" namespace strings, matching the
  // convention already used elsewhere in this file family.
  const namespace = type === "membership" ? "membership" : "package";
  // Columns come from the shared report catalogue (one name, width and
  // alignment per metric); this page keeps its own order and the shared
  // SORT_COMPARATORS as the single comparator source.
  const columns: ReportColumn<PackageAnalyticsRow>[] = [
    reportColumn<PackageAnalyticsRow>(type === "membership" ? "membership" : "package", {
      dataIndex: "name",
      render: (name: string, row) => (
        <Flex gap={8} align="center" wrap="wrap">
          <span style={{ fontWeight: 600 }}>{name}</span>
          <ProductStatusTag row={row} />
        </Flex>
      ),
    }),
    reportColumn<PackageAnalyticsRow>("promo_period", {
      key: "promo_period",
      render: (_, row) => {
        if (!row.is_promo) return "-";
        return (
          <Flex vertical gap={2} align="flex-start">
            <span>{formatDateRangeOrDash(row.promo_starts_at, row.promo_ends_at)}</span>
            {isPromoPeriodApplied(row) ? (
              <Tag
                style={{
                  background: colors.bgSoft,
                  color: colors.ink3,
                  borderColor: "transparent",
                  marginInlineEnd: 0,
                }}
                icon={<CheckCircleOutlined />}
              >
                {translate("analyticsPage.package.compare_period_applied")}
              </Tag>
            ) : promoPeriodRange(row) ? (
              <Button
                type="link"
                size="small"
                icon={<CalendarOutlined />}
                style={{ padding: 0, height: "auto" }}
                onClick={() => applyPromoPeriod(row)}
              >
                {translate("analyticsPage.package.compare_use_promo_period")}
              </Button>
            ) : null}
          </Flex>
        );
      },
    }),
    reportColumn<PackageAnalyticsRow>("unique_clients", {
      dataIndex: "unique_clients",
      // Reach, not buyers: it can exceed Sold once a purchase is shared.
      info: () => translate(`analyticsPage.${namespace}.col_unique_clients_info`),
      sorter: SORT_COMPARATORS.unique_clients,
    }),
    // No buyer split here (chris, 2026-10-17): New / Existing Clients sat
    // beside Unique Clients on a different basis (buyers vs everyone the
    // package reached) and "Klien Baru" already means four things across the
    // system. The Excel keeps Klien Baru / Klien Lama in its fuller row.
    reportColumn<PackageAnalyticsRow>("sold", {
      dataIndex: "sold",
      // Real sales only. Upgrades are counted separately: they are
      // expansion revenue rather than new units, but their fee is in
      // Penjualan and would otherwise have no visible source.
      info: () => translate(`analyticsPage.${namespace}.col_sold_info`),
      sorter: SORT_COMPARATORS.sold,
    }),
    // Always present, and paket only. A dash means no upgrades into that
    // package in this period; a count explains the part of Penjualan that
    // Terjual does not. Scoped to the admin's picks on all three surfaces,
    // since comparing the picked set is this report's whole job.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("upgrades", {
            dataIndex: "upgrades",
            render: (value: number) => <span style={tnum}>{formatUpgrades(value)}</span>,
            sorter: SORT_COMPARATORS.upgrades,
          }),
        ]),
    reportColumn<PackageAnalyticsRow>("sales", {
      dataIndex: "sales",
      render: (value: number | null) => <span style={tnum}>{formatMoneyOrDash(value)}</span>,
      sorter: SORT_COMPARATORS.sales,
    }),
    reportColumn<PackageAnalyticsRow>("last_purchase", {
      dataIndex: "last_purchase",
      sorter: SORT_COMPARATORS.last_purchase,
    }),
  ];

  return (
    <>
      <ReportPageHeading
        title={title}
        backTo={backTo}
        extra={
          selectedRows.length > 0 ? (
            <ExportMenu
              reportSlug={`bandingkan-${type}`}
              params={exportParams}
              pdf={{
                fileName: `spott-bandingkan-${type}-${exportStamp}.pdf`,
                render: async () => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: type === "membership" ? "bandingkan-membership" : "bandingkan-paket",
                    studio: authState.business?.name ?? "",
                    data: {
                      ...overview!,
                      range_label: rangeLabelDisplay ?? overview!.range_label,
                      table: selectedRows,
                    },
                    // Charts stay pick-order regardless of the table's
                    // active sort — same reasoning as on-screen (see
                    // chartRows above).
                    chartRows,
                  });
                },
              }}
            />
          ) : undefined
        }
      />
      <Flex vertical gap={20}>
        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
            <span style={{ fontSize: textPx(13), color: colors.ink2 }}>{rangeLabelDisplay}</span>
            <ConfigProvider theme={pillSegmentedTheme}>
              <Segmented
                value={allTime ? "all_time" : "range"}
                onChange={(value) => setAllTime(value === "all_time")}
                options={[
                  {
                    label: translate("analyticsPage.package.compare_mode_all_time"),
                    value: "all_time",
                  },
                  {
                    label: translate("analyticsPage.package.compare_mode_range"),
                    value: "range",
                  },
                ]}
              />
            </ConfigProvider>
          </Flex>
          {!allTime && (
            <Flex vertical gap={8}>
              <Flex align="center" gap={10} wrap="wrap">
                <RangePicker
                  style={{ width: "100%", maxWidth: 280 }}
                  format={DEFAULT_DATE_FORMAT}
                  placeholder={[
                    translate("analyticsPage.sales.custom_day_range_from"),
                    translate("analyticsPage.sales.custom_day_range_to"),
                  ]}
                  value={
                    customDayRange
                      ? [tzDate(customDayRange.from), tzDate(customDayRange.to)]
                      : null
                  }
                  onChange={(dates) => {
                    if (!dates || !dates[0] || !dates[1]) {
                      setCustomDayRange(null);
                      return;
                    }
                    setCustomDayRange({
                      from: dates[0].format(DEFAULT_DATE_SUBMIT_FORMAT),
                      to: dates[1].format(DEFAULT_DATE_SUBMIT_FORMAT),
                    });
                  }}
                  disabledDate={(current) => !!current && current > now().endOf("day")}
                />
                <RangeSegmented
                  value={range}
                  active={!customDayRange}
                  includeYtd={false}
                  onChange={(value) => {
                    setCustomDayRange(null);
                    setRange(value);
                  }}
                />
              </Flex>
              <span style={{ fontSize: textPx(12), color: colors.ink3 }}>
                {translate(
                  type === "membership"
                    ? "analyticsPage.membership.compare_range_note"
                    : "analyticsPage.package.compare_range_note",
                )}
              </span>
            </Flex>
          )}
        </Flex>
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder={translate("analyticsPage.package.compare_pick_items")}
          showSearch
          allowClear
          loading={isLoading}
          value={selectedIds}
          onChange={(value) => setSelectedIds(value)}
          filterOption={(input, option) =>
            ((option as CompareOption | undefined)?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          optionRender={renderOption}
          options={options}
        />
        {selectedRows.length > 0 ? (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card styles={{ body: { padding: 22 } }}>
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 600,
                      fontSize: textPx(16),
                      color: colors.ink,
                    }}
                  >
                    {translate("analyticsPage.package.compare_chart_revenue_heading")}
                  </div>
                  <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 4, marginBottom: 18 }}>
                    {translate("analyticsPage.package.compare_chart_revenue_desc")}
                  </div>
                  <PackageCompareRevenueChart rows={chartRows} />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card styles={{ body: { padding: 22 } }}>
                  <Flex justify="space-between" align="center" gap={12}>
                    <div>
                      <div
                        style={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 600,
                          fontSize: textPx(16),
                          color: colors.ink,
                        }}
                      >
                        {translate("analyticsPage.package.compare_chart_counts_heading")}
                      </div>
                      <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 4 }}>
                        {translate(
                          type === "membership"
                            ? "analyticsPage.membership.compare_chart_counts_desc"
                            : "analyticsPage.package.compare_chart_counts_desc",
                        )}
                      </div>
                    </div>
                    {/* Inline with the heading (not a separate row above the
                        chart, like RevenueCompositionChart/SessionsCompletedChart
                        do) so this card's header is the same height as the
                        revenue card's single-series one alongside it. */}
                    <Flex gap={12} flex="none" style={{ marginRight: 24 }}>
                      <ChartLegend color={colors.navy} label={translate("analyticsPage.columns.sold")} />
                      <ChartLegend
                        color={colors.ink4}
                        label={translate("analyticsPage.columns.unique_clients")}
                      />
                    </Flex>
                  </Flex>
                  <div style={{ marginTop: 18 }}>
                    <PackageCompareCountsChart rows={chartRows} />
                  </div>
                </Card>
              </Col>
            </Row>
            {selectedRows.length > COMPARE_CHART_MAX_ITEMS && (
              <span style={{ fontSize: textPx(12), color: colors.ink3 }}>
                {translate("analyticsPage.package.compare_chart_capped_note")}
              </span>
            )}
            <Card styles={{ body: { padding: 18 } }}>
              <ReportTable<PackageAnalyticsRow>
                rowKey="package_id"
                columns={columns}
                dataSource={selectedRows}
                pagination={false}
                sticky={false}
                onSortChange={onSortChange}
              />
            </Card>
          </>
        ) : (
          <Card style={{ borderRadius: 16, textAlign: "center" }}>
            <Flex vertical align="center" gap={8} style={{ padding: "24px 0" }}>
              <SwapOutlined style={{ fontSize: textPx(24), color: colors.ink3 }} />
              <span style={{ fontSize: textPx(13), color: colors.ink3 }}>
                {translate("analyticsPage.package.compare_empty_state")}
              </span>
            </Flex>
          </Card>
        )}
      </Flex>
    </>
  );
};

export default PackageComparisonContent;
